import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StoryModal = ({ story, onClose }) => {
  if (!story) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-5 flex items-center justify-between z-10">
          <h2 className="text-xl md:text-2xl font-bold text-foreground font-headline">
            Success Story Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors duration-300"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-4 md:p-6 lg:p-8">
          <div className="relative h-64 md:h-80 lg:h-96 rounded-lg overflow-hidden mb-6">
            <Image
              src={story?.image}
              alt={story?.imageAlt}
              className="w-full h-full object-cover"
            />
            {story?.videoAvailable && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-sm">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Play"
                  iconPosition="left"
                >
                  Watch Video Testimonial
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden border-2 border-primary">
              <Image
                src={story?.playerAvatar}
                alt={story?.playerAvatarAlt}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold text-foreground font-headline">
                {story?.playerName}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground">
                {story?.position} • {story?.nationality}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-muted rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">From</p>
              <p className="text-base md:text-lg font-semibold text-foreground">
                {story?.fromClub}
              </p>
              <p className="text-xs text-muted-foreground">{story?.fromLeague}</p>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <p className="text-xs text-muted-foreground mb-1">To</p>
              <p className="text-base md:text-lg font-semibold text-primary">
                {story?.toClub}
              </p>
              <p className="text-xs text-muted-foreground">{story?.toLeague}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-primary/10 rounded-lg">
              <p className="text-xl md:text-2xl font-bold text-primary font-headline">
                {story?.transferValue}
              </p>
              <p className="text-xs text-muted-foreground">Transfer Value</p>
            </div>
            <div className="text-center p-4 bg-secondary/10 rounded-lg">
              <p className="text-xl md:text-2xl font-bold text-secondary font-headline">
                {story?.careerGrowth}
              </p>
              <p className="text-xs text-muted-foreground">Career Growth</p>
            </div>
            <div className="text-center p-4 bg-success/10 rounded-lg">
              <p className="text-xl md:text-2xl font-bold text-success font-headline">
                {story?.appearances}
              </p>
              <p className="text-xs text-muted-foreground">Appearances</p>
            </div>
            <div className="text-center p-4 bg-accent/10 rounded-lg">
              <p className="text-xl md:text-2xl font-bold text-accent font-headline">
                {story?.goalsAssists}
              </p>
              <p className="text-xs text-muted-foreground">Goals/Assists</p>
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg md:text-xl font-bold text-foreground font-headline mb-3 flex items-center gap-2">
              <Icon name="BookOpen" size={20} />
              The Journey
            </h4>
            <p className="text-sm md:text-base text-foreground leading-relaxed whitespace-pre-line">
              {story?.fullStory}
            </p>
          </div>

          <div className="mb-6">
            <h4 className="text-lg md:text-xl font-bold text-foreground font-headline mb-3 flex items-center gap-2">
              <Icon name="Award" size={20} />
              Key Achievements
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {story?.achievements?.map((achievement, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-success/10 rounded-lg"
                >
                  <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
                  <span className="text-sm md:text-base text-foreground">
                    {achievement}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <h4 className="text-lg md:text-xl font-bold text-foreground font-headline mb-3 flex items-center gap-2">
              <Icon name="Target" size={20} />
              Challenges Overcome
            </h4>
            <div className="space-y-3">
              {story?.challenges?.map((challenge, index) => (
                <div key={index} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                  <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
                  <div>
                    <p className="text-sm md:text-base font-semibold text-foreground mb-1">
                      {challenge?.title}
                    </p>
                    <p className="text-xs md:text-sm text-muted-foreground">
                      {challenge?.solution}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-primary/10 rounded-lg p-4 md:p-6 mb-6">
            <h4 className="text-lg md:text-xl font-bold text-foreground font-headline mb-3 flex items-center gap-2">
              <Icon name="Quote" size={20} />
              Player Testimonial
            </h4>
            <p className="text-sm md:text-base text-foreground italic leading-relaxed">
              "{story?.testimonial}"
            </p>
            <p className="text-xs md:text-sm text-muted-foreground mt-2">
              — {story?.playerName}
            </p>
          </div>

          <div className="bg-secondary/10 rounded-lg p-4 md:p-6">
            <h4 className="text-lg md:text-xl font-bold text-foreground font-headline mb-3 flex items-center gap-2">
              <Icon name="Building2" size={20} />
              Club Official Feedback
            </h4>
            <p className="text-sm md:text-base text-foreground italic leading-relaxed">
              "{story?.clubFeedback}"
            </p>
            <p className="text-xs md:text-sm text-muted-foreground mt-2">
              — {story?.clubOfficialName}, {story?.clubOfficialRole}
            </p>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 md:p-5 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            variant="outline"
            iconName="Share2"
            iconPosition="left"
          >
            Share Story
          </Button>
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
          >
            Contact for Similar Success
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StoryModal;